



import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Box, CircularProgress } from "@mui/material";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import TruncatedCell from "../../../../components/TruncatedCell.js";
import TaskDetails from "./TaskDetails";   // 👈 import details
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";

const localizer = momentLocalizer(moment);

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const localDateTime = convertUTCToLocalDateTime(dateString, { locale: "en-GB" });
  return localDateTime === "—" ? "-" : localDateTime;
};

const formatTaskTimeRange = (startAt, endAt) => {
  const start = formatDateTime(startAt);
  const end = formatDateTime(endAt);
  if (start === "-" && end === "-") return "-";
  if (start === "-") return end;
  if (end === "-") return start;
  return `${start} - ${end}`;
};

const CalendarView = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [taskDetails, setTaskDetails] = useState(null);

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  // fetch all tasks for calendar
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/task-management/worker/tasks?page=1&page_size=50&show_history=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();

        if (!data.error) {
          const mappedEvents = data.data.tasks.map((task) => ({
            id: task.id,
            title: task.title,
            start: moment.utc(task.start_at).local().toDate(),
            end: moment.utc(task.end_at).local().toDate(),
            timeRange: formatTaskTimeRange(task.start_at, task.end_at),
            status: task.status,
            worker: task.assigned_by_name || "—",
            location: task.location_address,
          }));
          setEvents(mappedEvents);
        }
      } catch (err) {
        console.error("❌ Error fetching tasks:", err);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchTasks();
  }, [token]);

  // fetch task details
  const fetchTaskDetails = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/task-management/worker/tasks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.error) {
        setTaskDetails({
          ...data.data.task,
          assignments: data.data.assignments || [],
          comments: data.data.comments || [],
        });
      }
    } catch (err) {
      console.error("❌ Error fetching task details:", err);
    }
  };

  // handle event click
  const handleSelectTask = (id) => {
    setSelectedTaskId(id);
    fetchTaskDetails(id);
  };

  // back from details
  const handleBack = () => {
    setSelectedTaskId(null);
    setTaskDetails(null);
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = "#90caf9";
    if (event.status === "completed") backgroundColor = "#a5d6a7";
    if (event.status === "assigned") backgroundColor = "#64b5f6";
    if (event.status === "cancelled") backgroundColor = "#ef9a9a";
    if (event.status === "in_progress") backgroundColor = "#ffcc80";
    if (event.status === "not_done") backgroundColor = "#b0bec5";

    return {
      style: {
        backgroundColor,
        color: "white",
        borderRadius: "6px",
        border: "0px",
        display: "block",
        paddingLeft: "4px",
      },
    };
  };

  // 🔹 if a task is selected → show TaskDetails
  if (selectedTaskId) {
    return <TaskDetails task={taskDetails} onBack={handleBack} />;
  }

  // 🔹 else show Calendar
  return (
    <Box sx={{ height: "80vh", p: 2, background: "#fff", borderRadius: "8px" }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Calendar
          onSelectEvent={(event) => handleSelectTask(event.id)}
          localizer={localizer}
          events={events}
          defaultView="month"
          views={["day", "week", "month"]}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%" }}
          eventPropGetter={eventStyleGetter}
          components={{
            event: ({ event }) => (
              <Box
                sx={{
                  lineHeight: 1.25,
                  overflow: "hidden",
                  width: "100%",
                }}
                title={`${event.title}\n${event.timeRange}`}
              >
                <TruncatedCell
                  text={event.title}
                  maxLength={22}
                  sx={{ color: "white", fontSize: 12, maxWidth: "100%" }}
                />
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontSize: 10,
                    color: "rgba(255,255,255,0.95)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontFamily: "Poppins, sans-serif",
                    mt: 0.25,
                  }}
                >
                  {event.timeRange}
                </Box>
              </Box>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default CalendarView;

